import { useState } from "react";

import { Icon } from "../../components/Icon";
import { COMPANY, WHATSAPP } from "../../config/company";
import { CompanyLayout, InfoSection, SafetyNote } from "./CompanyLayout";

// Prefilled emails, so people send what we need the first time.
const BODY =
  "WhatsApp number:\nReference (from your receipt, or an EJ-, DS- or SP- code):\nWhat happened:\n";

const TOPICS: { subject: string; label: string; body: string }[] = [
  {
    label: "Payment or refund",
    subject: "Payment problem",
    body: "Charged but nothing arrived, a refund you’re waiting for, or a charge you don’t recognise.",
  },
  {
    label: "Card",
    subject: "Card problem",
    body: "Your card keeps being declined, Hethera says it needs attention, or you want to remove it.",
  },
  {
    label: "Ejor, Dash or schedules",
    subject: "Ejor / Dash / schedule",
    body: "A request, gift or scheduled payment that didn’t go the way you expected.",
  },
  {
    label: "Account & PIN",
    subject: "Account or PIN",
    body: "Forgotten PIN, a locked account, a lost or stolen phone, or a change of number.",
  },
  {
    label: "Delete my data",
    subject: "Delete my Hethera account",
    body: "Close your account and delete the personal data we hold. Some payment records must be kept by law.",
  },
  {
    label: "Something else",
    subject: "Question for Hethera",
    body: "Feedback, business enquiries, press, or anything not listed here.",
  },
];

function mailto(subject: string): string {
  return `mailto:${COMPANY.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(BODY)}`;
}

function CopyEmail() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(COMPANY.supportEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the address is on screen to copy by hand.
    }
  };
  return (
    <button type="button" className="btn btn-ghost" onClick={copy}>
      <Icon name={copied ? "check" : "copy"} size={18} />
      {copied ? "Copied" : "Copy address"}
    </button>
  );
}

const QUICK_ANSWERS = [
  {
    q: "I was charged but nothing arrived.",
    a: "You don’t need to ask for a refund. When a purchase isn’t delivered, the full amount, fee included, goes back to your card automatically and we tell you in the chat. Some purchases take a little while to confirm; we keep checking and message you either way. Banks can take a few working days to show the refund.",
  },
  {
    q: "My card was declined.",
    a: "We never try a declined card again on our own. Check with your bank, then ask Hethera to try again and confirm with your PIN. If your bank asks for an OTP or extra confirmation, re-add the card through the link Hethera sends.",
  },
  {
    q: "I forgot my PIN or lost my phone.",
    a: "Email us from any device. We’ll lock the account and help you set a new PIN. Too many wrong PIN attempts lock the account automatically.",
  },
];

export function SupportPage() {
  return (
    <CompanyLayout
      path="/support"
      eyebrow="Email support"
      title="We’re a message away."
      intro={
        <>
          Email us about a payment, a refund, your card or your account. A
          person reads every message, {COMPANY.supportHours}.
        </>
      }
      aside={
        <div className="email-card">
          <span className="email-card__label">Support email</span>
          <a className="email-card__address" href={mailto("Help with Hethera")}>
            {COMPANY.supportEmail}
          </a>
          <div className="email-card__actions">
            <a className="btn btn-dark" href={mailto("Help with Hethera")}>
              <Icon name="mail" size={18} />
              Write an email
            </a>
            <CopyEmail />
          </div>
          <span className="email-card__hours">
            <Icon name="clock" size={16} />
            Replies {COMPANY.supportHours}
          </span>
        </div>
      }
    >
      <InfoSection
        title="What is it about?"
        lede="Choose a topic and your email opens with the subject and the details we need already in place."
      >
        <ul className="topic-grid">
          {TOPICS.map((t) => (
            <li key={t.label}>
              <a href={mailto(t.subject)}>
                <strong>{t.label}</strong>
                <span>{t.body}</span>
                <Icon name="arrow" size={18} />
              </a>
            </li>
          ))}
        </ul>
      </InfoSection>

      <InfoSection
        title="Help us help you faster"
        lede="Three things that let us find your payment straight away."
      >
        <ol className="principles principles--compact">
          <li>
            <h3>Your WhatsApp number</h3>
            <p>
              The number you use with Hethera, in any format. Your account is
              linked to it.
            </p>
          </li>
          <li>
            <h3>The reference</h3>
            <p>
              It’s on the receipt in the chat. For an Ejor, a Dash gift or a
              scheduled payment, its code (EJ-, DS- or SP-) works too.
            </p>
          </li>
          <li>
            <h3>What happened</h3>
            <p>
              A line or two is enough: what you tried to pay, roughly when, and
              what you saw.
            </p>
          </li>
        </ol>
        <SafetyNote />
      </InfoSection>

      <InfoSection
        title="Before you write"
        lede="The answers we send most often."
      >
        <div className="faq">
          {QUICK_ANSWERS.map((f) => (
            <details key={f.q}>
              <summary>
                {f.q}
                <span className="faq__plus" aria-hidden="true">
                  +
                </span>
              </summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
        <p className="info-more">
          More answers in the <a href="/#faq">FAQ</a>. Refunds are covered in
          the <a href="/refund-policy">Refund Policy</a>. Or just ask in the{" "}
          <a href={WHATSAPP.href}>WhatsApp chat</a>.
        </p>
      </InfoSection>
    </CompanyLayout>
  );
}
